// useRoomAlerts — the in-tab alert + sound for GROUP chat (contract items 5
// and 9), as ONE app-wide subscription.
//
// Why one and not one-per-room: the alert has to fire for a room the reader
// does not have open, which is every room but one. RLS already limits
// `room_messages` realtime to rooms I can read, so a single unfiltered channel
// is both the smallest and the most complete subscription available — and it
// is the same socket the rest of the app shares.
//
// Sources, in the order the contract names them:
//   messages   → `room_messages` INSERT, kind='user', not mine.
//   reactions  → `room_message_reactions` INSERT (the normalized table, 0056);
//                when it is not there the same events still arrive as a
//                `room_messages` UPDATE whose `reactions` jsonb grew, so the
//                fallback diffs that map against the last copy this session
//                saw. Both paths share one dedupe key, so applying 0056 does
//                not double every ping.
//
// LIVE V2 (FEATURES.liveV2Rooms, migration 0079): the same alerts, driven by
// events the DATABASE publishes on the private topic user:<id> — `room_message`
// (members minus sender, minus muted unless mentioned, mention verdict
// attached) and `room_reaction` (to the author) — instead of the two
// whole-table bindings above, which cost Realtime a policy check per
// subscriber per change. The DECISIONS are the same functions in roomAlerts.ts
// (own message never; the room on screen in a focused tab never; muted only
// beaten by @me / @all). With the flag on and the schema present the two
// postgres_changes channels are NOT opened; a database without 0079, or a
// refused user channel, falls back to them silently.
//
// No initial fetch: an alert is about what happens WHILE the tab is open, so
// everything older than mount is ignored by construction.
//
// There IS a polling fallback (FEATURES.chatParity, services/chat/liveness.ts):
// a socket that dies without saying so takes the alerts down with it, silently
// — the failure mode this whole feature exists to fix. While the channel is
// unhealthy AND the tab is in the foreground, `room_sync_since` fetches what
// changed and the rows go through the SAME handler the realtime payloads do,
// so the dedupe (seenReactions / messageCache) covers both sources.

import { useEffect, useRef } from 'react';
import type { ChatRoom, User } from '../types';
import { supabase, msFromISO0, USE_SUPABASE, type RealtimePayload } from './supabaseClient';
import { FEATURES } from './features';
import { createLiveness } from './chat/liveness';
import { fetchMessageMentions, roomSyncSince } from './roomActionsParity';
import { isRoomMuted } from './roomMuteState';
import { isRoomOpenAndFocused } from './openRoom';
import {
  diffReactionAdds,
  mentionsMeFromRows,
  mentionsMeInBody,
  reactionAlertContent,
  reactionKey,
  roomMessageAlertContent,
  shouldAlertForReaction,
  shouldAlertForRoomMessage,
  type ReactionMap,
} from './roomAlerts';
import {
  joinUserChannel, onUserEvent, onUserChannelJoin, onUserChannelUnavailable, isUserChannelJoined,
} from './live/userChannel';
import { probeRoomLiveSchema } from './live/roomChannel';

export interface RoomAlert {
  title: string;
  body: string;
  roomId: string;
  messageId: string;
  /** The message `@`s me or `@all` — the reason it beat a mute. */
  mention: boolean;
  kind: 'message' | 'reaction';
}

export interface UseRoomAlertsOptions {
  /** FEATURES.chatParity && USE_SUPABASE && signed in. */
  enabled: boolean;
  meId: string | null | undefined;
  rooms: ChatRoom[];
  users: User[];
  onAlert: (alert: RoomAlert) => void;
}

/** Alerts ride user:<id> events instead of the whole-table bindings. */
export const ROOM_ALERTS_LIVE = FEATURES.liveV2Rooms && USE_SUPABASE;
export const LIVE_MESSAGE_EVENT = 'room_message';
export const LIVE_REACTION_EVENT = 'room_reaction';

/** Most recent messages kept for the reaction fallback. Bounded so a long
 *  session cannot grow it without limit; the newest are the ones anyone
 *  reacts to. */
const MESSAGE_CACHE_MAX = 400;
const SEEN_REACTIONS_MAX = 500;
const SEEN_MESSAGES_MAX = 500;
/** Messages looked up for a reaction and found not to be mine (or not found at
 *  all). Remembered so a burst of reactions on the same old message costs one
 *  round trip, not one per emoji. */
const UNKNOWN_CACHE_MAX = 200;
/**
 * Slack the "older than this tab" guard allows for clock skew. `since` is the
 * CLIENT's clock and `created_at` is the SERVER's; a laptop running a minute
 * fast would otherwise drop every alert for its first minute, silently — the
 * exact "alerts produce nothing" failure this feature exists to fix.
 */
const CLOCK_SKEW_MS = 5 * 60 * 1000;
/** Fallback-poll cadence for the APP-WIDE listener. Deliberately slower than
 *  the open room's (services/chat/liveness defaults): this one covers every
 *  room at once and nobody is watching it, so it trades latency for load. */
const ALERTS_TICK_MS = 5000;
const ALERTS_MIN_BACKOFF_MS = 15000;
const ALERTS_MAX_BACKOFF_MS = 60000;
/** Silence this listener tolerates from a SUBSCRIBED channel before it probes.
 *  Longer than the open room's 45 s on purpose: a healthy quiet channel still
 *  costs one probe per window, and this listener runs in EVERY signed-in tab in
 *  the fleet, all day. Two minutes of extra latency in a rare degraded mode is
 *  worth halving that standing cost. */
const ALERTS_SILENT_AFTER_MS = 120000;
/** How much backlog ONE catch-up poll may alert for.
 *
 *  The cursor only advances on a realtime payload or a completed poll, and
 *  neither happens while the tab is hidden — so a laptop that slept overnight
 *  with a dead socket wakes up holding a cursor from last night. Unclamped,
 *  its first poll would pop a notification (and a sound) for every message the
 *  fleet sent since. An alert is about what is happening NOW — the same reason
 *  `since`/CLOCK_SKEW_MS exists — so the catch-up reaches back five minutes and
 *  no further. Older traffic is caught up by opening the room, which is what
 *  the unread badge is for. */
const ALERTS_CATCHUP_MAX_MS = 5 * 60 * 1000;

interface CachedMessage {
  roomId: string;
  senderId: string;
  reactions: ReactionMap;
}

function remember<K, V>(map: Map<K, V>, key: K, value: V, max: number): void {
  map.delete(key);
  map.set(key, value);
  while (map.size > max) {
    const oldest = map.keys().next();
    if (oldest.done) break;
    map.delete(oldest.value);
  }
}

/** The same insertion-ordered eviction, for the plain key sets. */
function rememberKey(set: Set<string>, key: string, max: number): void {
  set.delete(key);
  set.add(key);
  while (set.size > max) {
    const oldest = set.values().next();
    if (oldest.done) break;
    set.delete(oldest.value);
  }
}

export function useRoomAlerts({ enabled, meId, rooms, users, onAlert }: UseRoomAlertsOptions): void {
  // Everything the handlers read lives behind a ref: the rooms array is a new
  // object on every realtime patch anywhere in the app, and tearing the socket
  // down each time would be a reconnect storm.
  const roomsRef = useRef(rooms);
  roomsRef.current = rooms;
  const usersRef = useRef(users);
  usersRef.current = users;
  const onAlertRef = useRef(onAlert);
  onAlertRef.current = onAlert;
  const meRef = useRef(meId);
  meRef.current = meId;

  useEffect(() => {
    if (!enabled || !meId) return;
    let dead = false;
    const since = Date.now();
    const messageCache = new Map<string, CachedMessage>();
    const seenReactions = new Set<string>();
    // Message ids already alerted on: a channel re-join replays recent INSERTs,
    // and one message must ping once, not once per replay.
    const seenMessages = new Set<string>();
    const notMine = new Set<string>();
    const inFlight = new Set<string>();

    const roomOf = (roomId: string): ChatRoom | undefined =>
      roomsRef.current.find((r) => r.id === roomId);
    const iAmMember = (room: ChatRoom | undefined): boolean =>
      !!room && !!meRef.current && !!room.memberIds?.includes(meRef.current);
    const memberUsers = (room: ChatRoom): Array<{ id: string; name?: string }> =>
      room.memberIds
        .map((id) => usersRef.current.find((u) => u.id === id))
        .filter((u): u is User => !!u);
    const nameOf = (userId: string): string =>
      usersRef.current.find((u) => u.id === userId)?.name || '';

    const fire = (alert: RoomAlert) => {
      if (dead) return;
      onAlertRef.current(alert);
    };

    const onReactionAdded = (
      messageId: string,
      roomId: string,
      reactorId: string,
      emoji: string,
      reactorName?: string,
    ) => {
      const key = reactionKey(messageId, reactorId, emoji);
      if (seenReactions.has(key)) return;
      const room = roomOf(roomId);
      if (!iAmMember(room)) return;
      const ok = shouldAlertForReaction({
        mineMessage: true, // the callers only reach here for my own messages
        reactorIsMe: reactorId === meRef.current,
        muted: isRoomMuted(roomId),
        roomOpenAndFocused: isRoomOpenAndFocused(roomId),
      });
      // Remember it either way — a suppressed reaction must not ping later
      // through the other source.
      rememberKey(seenReactions, key, SEEN_REACTIONS_MAX);
      if (!ok) return;
      const content = reactionAlertContent({
        roomName: room?.name,
        reactorName: nameOf(reactorId) || reactorName,
        emoji,
      });
      fire({ ...content, roomId, messageId, mention: false, kind: 'reaction' });
    };

    /**
     * Who sent `messageId`, for a reaction that arrived on the normalized
     * table. The in-session cache only holds messages this tab watched go by,
     * so the common real case — someone reacts to something I wrote YESTERDAY
     * — misses it, and the jsonb fallback deliberately says nothing on first
     * sight of a message. One bounded lookup closes that hole: it runs only
     * for a reaction by someone else on a message this session has not seen,
     * and its answer (mine / not mine) is remembered either way.
     */
    const resolveMessage = async (messageId: string): Promise<CachedMessage | null> => {
      const cached = messageCache.get(messageId);
      if (cached) return cached;
      if (notMine.has(messageId) || inFlight.has(messageId)) return null;
      inFlight.add(messageId);
      try {
        const { data, error } = await supabase()
          .from('room_messages')
          .select('id, room_id, sender_id')
          .eq('id', messageId)
          .maybeSingle();
        if (dead) return null;
        // A realtime row may have landed while this was in flight; it knows
        // more (the reactions map), so it wins.
        const raced = messageCache.get(messageId);
        if (raced) return raced;
        if (error || !data) { rememberKey(notMine, messageId, UNKNOWN_CACHE_MAX); return null; }
        const entry: CachedMessage = {
          roomId: String((data as any).room_id ?? ''),
          senderId: String((data as any).sender_id ?? ''),
          // `undefined` on purpose: "first sight", so the jsonb diff never
          // replays the reactions this row already carried.
          reactions: undefined,
        };
        if (entry.senderId !== meRef.current) { rememberKey(notMine, messageId, UNKNOWN_CACHE_MAX); return null; }
        remember(messageCache, messageId, entry, MESSAGE_CACHE_MAX);
        return entry;
      } catch {
        return null;
      } finally {
        inFlight.delete(messageId);
      }
    };

    const onMessageRow = async (row: any, isInsert: boolean) => {
      const id = String(row?.id ?? '');
      const roomId = String(row?.room_id ?? '');
      if (!id || !roomId) return;
      const room = roomOf(roomId);
      if (!iAmMember(room)) return;
      const senderId = String(row.sender_id ?? '');
      const prev = messageCache.get(id);
      remember<string, CachedMessage>(
        messageCache,
        id,
        { roomId, senderId, reactions: row.reactions ?? null },
        MESSAGE_CACHE_MAX,
      );

      // ── Reaction fallback: the jsonb grew on MY message ────────────────
      if (!isInsert && senderId === meRef.current) {
        for (const add of diffReactionAdds(prev?.reactions, row.reactions ?? null, meRef.current)) {
          onReactionAdded(id, roomId, add.userId, add.emoji);
        }
      }
      if (!isInsert) return;

      // ── New message ───────────────────────────────────────────────────
      if (row.kind === 'system') return;
      if (row.deleted) return;
      if (seenMessages.has(id)) return;
      rememberKey(seenMessages, id, SEEN_MESSAGES_MAX);
      const at = msFromISO0(row.created_at);
      if (at && at < since - CLOCK_SKEW_MS) return; // replayed history, not news
      const mine = senderId === meRef.current;
      const roomOpenAndFocused = isRoomOpenAndFocused(roomId);
      const muted = isRoomMuted(roomId);
      const body = row.body ?? '';
      let mentionsMe = mentionsMeInBody(body, {
        members: room ? memberUsers(room) : [],
        meId: meRef.current,
      });
      // The mention ROWS are the authority (they carry `@all` and mentions of
      // people whose display name the body abbreviates); the body parse above
      // is the fallback for a database without 0056. Only a muted room needs
      // the round trip — anywhere else the answer cannot change the outcome.
      if (muted && !mine && !roomOpenAndFocused) {
        const rows = await fetchMessageMentions(id);
        if (dead) return;
        if (rows) mentionsMe = mentionsMeFromRows(rows, meRef.current);
      }
      if (!shouldAlertForRoomMessage({ mine, muted, mentionsMe, roomOpenAndFocused })) return;
      const content = roomMessageAlertContent({
        roomName: room?.name,
        senderName: row.sender_name ?? '',
        body,
        hasAttachment: !!row.has_attachment || !!row.image_url,
      });
      fire({ ...content, roomId, messageId: id, mention: mentionsMe, kind: 'message' });
    };

    // ── Live v2: the server already chose the recipients ───────────────────
    // `room_message` reaches every member but the sender, and a muted member
    // only when mentioned; the payload carries the mention verdict from the
    // mention rows, so no round trip. The client still runs the same rule —
    // it is the one that knows which room is on screen.
    const onLiveMessage = (p: any) => {
      const id = String(p?.message_id ?? '');
      const roomId = String(p?.room_id ?? '');
      if (!id || !roomId) return;
      const senderId = String(p.sender_id ?? '');
      const room = roomOf(roomId);
      if (!messageCache.has(id)) {
        remember<string, CachedMessage>(messageCache, id, { roomId, senderId, reactions: undefined }, MESSAGE_CACHE_MAX);
      }
      if (seenMessages.has(id)) return;
      rememberKey(seenMessages, id, SEEN_MESSAGES_MAX);
      const at = msFromISO0(p.created_at);
      if (at && at < since - CLOCK_SKEW_MS) return; // replayed history, not news
      const mine = senderId === meRef.current;
      const roomOpenAndFocused = isRoomOpenAndFocused(roomId);
      const muted = isRoomMuted(roomId);
      const preview = typeof p.preview === 'string' ? p.preview : '';
      let mentionsMe = p.mention === 'me' || p.mention === 'all';
      // No verdict = a message written by the legacy direct insert (no mention
      // rows); the body parse is what today's path uses for those too.
      if (!mentionsMe && p.mention == null) {
        mentionsMe = mentionsMeInBody(preview, { members: room ? memberUsers(room) : [], meId: meRef.current });
      }
      if (!shouldAlertForRoomMessage({ mine, muted, mentionsMe, roomOpenAndFocused })) return;
      const content = roomMessageAlertContent({
        roomName: room?.name,
        senderName: p.sender_name ?? '',
        body: preview,
        hasAttachment: !!p.has_attachment,
      });
      fire({ ...content, roomId, messageId: id, mention: mentionsMe, kind: 'message' });
    };
    const onLiveReaction = (p: any) => {
      const messageId = String(p?.message_id ?? '');
      const roomId = String(p?.room_id ?? '');
      const reactorId = String(p?.user_id ?? '');
      const emoji = String(p?.emoji ?? '');
      if (!messageId || !roomId || !reactorId || !emoji) return;
      if (reactorId === meRef.current) return;
      // Only ever sent to the author, so this IS my message.
      if (!messageCache.has(messageId)) {
        remember<string, CachedMessage>(messageCache, messageId, { roomId, senderId: meRef.current || '', reactions: undefined }, MESSAGE_CACHE_MAX);
      }
      onReactionAdded(messageId, roomId, reactorId, emoji, typeof p.reactor_name === 'string' ? p.reactor_name : undefined);
    };

    // ── Polling fallback (parity) ─────────────────────────────────────────
    const live = FEATURES.chatParity ? createLiveness({
      silentAfterMs: ALERTS_SILENT_AFTER_MS,
      minBackoffMs: ALERTS_MIN_BACKOFF_MS,
      maxBackoffMs: ALERTS_MAX_BACKOFF_MS,
    }) : null;
    let lastSyncAt = since;
    let polling = false;
    const pollNow = async () => {
      if (dead || polling || !live) return;
      const roomIds = roomsRef.current.filter((r) => iAmMember(r)).map((r) => r.id);
      if (!roomIds.length) { live.onPolled(Date.now(), true); return; }
      polling = true;
      const startedAt = Date.now();
      try {
        // No fallback read: this listener has no list to rebuild, and a
        // database without 0060 simply keeps the realtime-only behaviour.
        const cursor = Math.max(lastSyncAt - 5000, startedAt - ALERTS_CATCHUP_MAX_MS, 0);
        const res = await roomSyncSince(roomIds, cursor, () => {});
        if (dead) return;
        for (const r of res?.messages ?? []) {
          // A row this session has never seen is news (an INSERT we missed);
          // one it has seen is a change to it — exactly the split the realtime
          // handler makes, so one message can never alert twice.
          await onMessageRow(r, !messageCache.has(String(r?.id ?? '')));
          if (dead) return;
        }
        // The server's clock when the RPC gave one — `p_since` is compared
        // against `created_at`, so a fast browser clock would otherwise ask
        // for the future and alert on nothing ever again (see roomSyncSince).
        lastSyncAt = res?.at || startedAt;
        // A missing RPC (production, pre-0060) has nothing to offer: back off
        // to the maximum instead of re-asking every 15 s.
        live.onPolled(Date.now(), res !== null);
      } catch {
        live.onPolled(Date.now(), false); // silent: background work
      } finally {
        polling = false;
      }
    };
    // Live v2 feeds the liveness clock from the user channel's join state
    // (transitions only — the clock measures silence since the last one).
    let liveJoined = false;
    let liveBound = false;
    const tick = () => {
      if (dead || !live) return;
      const now = Date.now();
      if (liveBound) {
        const joined = isUserChannelJoined();
        if (joined !== liveJoined) { liveJoined = joined; live.onStatus(joined ? 'SUBSCRIBED' : 'CLOSED', now); }
      }
      const due = live.nextPollAt(now);
      if (due !== null && due <= now) void pollNow();
    };
    const syncForeground = () => {
      if (!live || dead) return;
      live.onForeground(typeof document === 'undefined' || document.visibilityState !== 'hidden', Date.now());
    };

    let messagesChannel: any = null;
    let reactionsChannel: any = null;
    // Today's path: two whole-table bindings.
    const bindLegacy = () => {
      if (dead || messagesChannel) return;
      messagesChannel = supabase()
        .channel(`room_alerts_msgs_${Math.random().toString(36).slice(2, 8)}`)
        .on(
          'postgres_changes',
          { event: '*', schema: 'public', table: 'room_messages' },
          (payload?: RealtimePayload) => {
            live?.onEvent(Date.now());
            lastSyncAt = Date.now();
            const type = payload?.eventType;
            if (type !== 'INSERT' && type !== 'UPDATE') return;
            void onMessageRow(payload?.new, type === 'INSERT');
          },
        )
        .subscribe((status: string) => { live?.onStatus(status, Date.now()); });

      // Primary reaction source. Subscribing to a table that does not exist yet
      // is inert (it simply never delivers), which is exactly the degradation
      // the contract asks for before 0056 is applied.
      reactionsChannel = supabase()
        .channel(`room_alerts_reacts_${Math.random().toString(36).slice(2, 8)}`)
        .on(
          'postgres_changes',
          { event: 'INSERT', schema: 'public', table: 'room_message_reactions' },
          (payload?: RealtimePayload) => {
            const row: any = payload?.new;
            if (!row) return;
            const messageId = String(row.message_id ?? '');
            const reactorId = String(row.user_id ?? '');
            const emoji = String(row.emoji ?? '');
            if (!messageId || !reactorId || !emoji) return;
            if (reactorId === meRef.current) return;
            // Only my own messages are worth a ping. The in-session cache answers
            // "whose message is this?" for free; `resolveMessage` pays one bounded
            // lookup for the (very common) case of a reaction to something I wrote
            // before this tab opened, which the jsonb fallback cannot report.
            void (async () => {
              const cached = await resolveMessage(messageId);
              if (dead || !cached || cached.senderId !== meRef.current) return;
              onReactionAdded(messageId, cached.roomId, reactorId, emoji);
            })();
          },
        )
        .subscribe();
    };

    // Live v2: user:<id> events. A refused user channel (0076 policy missing,
    // Realtime authorization off) hands over to the legacy bindings.
    let offLive: (() => void) | null = null;
    const bindLive = () => {
      if (dead || offLive || messagesChannel) return;
      const offs: Array<() => void> = [];
      offs.push(onUserEvent(LIVE_MESSAGE_EVENT, (p) => {
        live?.onEvent(Date.now());
        lastSyncAt = Date.now();
        onLiveMessage(p);
      }));
      offs.push(onUserEvent(LIVE_REACTION_EVENT, (p) => { live?.onEvent(Date.now()); onLiveReaction(p); }));
      offs.push(onUserChannelJoin(() => {
        if (dead || !live) return;
        liveJoined = true;
        live.onStatus('SUBSCRIBED', Date.now());
      }));
      offs.push(onUserChannelUnavailable(() => {
        if (dead) return;
        const off = offLive;
        offLive = null;
        liveBound = false;
        if (off) off();
        bindLegacy();
      }));
      offs.push(joinUserChannel(meId));
      liveBound = true;
      offLive = () => { for (const off of offs.reverse()) { try { off(); } catch { /* ignore */ } } };
    };

    if (ROOM_ALERTS_LIVE) {
      void probeRoomLiveSchema().then((present) => {
        if (dead) return;
        if (present) bindLive(); else bindLegacy();
      });
    } else {
      bindLegacy();
    }

    let pollTimer: ReturnType<typeof setInterval> | null = null;
    if (live) {
      syncForeground();
      pollTimer = setInterval(tick, ALERTS_TICK_MS);
      document.addEventListener('visibilitychange', syncForeground);
      window.addEventListener('focus', syncForeground);
    }

    return () => {
      dead = true;
      if (pollTimer) clearInterval(pollTimer);
      if (live) {
        document.removeEventListener('visibilitychange', syncForeground);
        window.removeEventListener('focus', syncForeground);
      }
      if (offLive) offLive();
      if (messagesChannel) supabase().removeChannel(messagesChannel);
      if (reactionsChannel) supabase().removeChannel(reactionsChannel);
    };
    // rooms/users/onAlert ride refs on purpose — see above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, meId]);
}
